import React, { useState } from 'react';
import { Button } from '../../../../../../shared/ui/Button';
import { Bage, DiscountBage } from '../../subscription-modal/compose/bage';
import { PaymentModal } from '../../subscription-modal/compose/modals/payment/payment';
import { useAppDispatch, useAppSelector } from '../../../../../../app/store';
import {
    fetchSubscription, selectSubscription, selectSubscriptionError, selectSubscriptionLoading,
    Subscription, SubscriptionStatus,
} from '../../../../../../entities/subscription';
import '../lib/index.css';

type CardProps = {
    subscription: Subscription;
    onPayment: (period: 1 | 12) => void;
    trialOffer?: 'none' | 'single' | 'periods';
    now?: number;
};
const day = 86400000;
const timestamp = (date: string | null) => date ? Date.parse(date) : NaN;
const daysLabel = (count: number) => count % 10 === 1 && count % 100 !== 11 ? 'день'
    : [2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100) ? 'дня' : 'дней';

export function UserSubscription({ subscription, onPayment, trialOffer = 'periods', now = Date.now() }: CardProps) {
    const status = subscription.subscriptionStatusId;
    const trial = status === SubscriptionStatus.TrialActive || status === SubscriptionStatus.TrialExpiring;
    const expired = status === SubscriptionStatus.Basic && subscription.billingPeriod != null;
    const basic = status === SubscriptionStatus.Basic && !expired;
    const soon = status === SubscriptionStatus.ProExpiring || status === SubscriptionStatus.TrialExpiring;
    const failed = status === SubscriptionStatus.ProPaymentFailed;
    const end = timestamp(subscription.dateEnd);
    const start = timestamp(subscription.dateStart);
    const remaining = Number.isFinite(end) ? Math.max(0, Math.ceil((end - now) / day)) : null;
    const total = Number.isFinite(start) && Number.isFinite(end) && end > start ? Math.ceil((end - start) / day) : null;
    const progress = basic ? 0 : expired ? 100 : total !== null && remaining !== null
        ? Math.max(0, Math.min(100, (1 - remaining / total) * 100)) : 0;
    const showOffer = (trial && trialOffer !== 'none') || expired || soon || failed
        || status === SubscriptionStatus.ProCancelled;
    const annual = subscription.billingPeriod === 12;

    return (
        <section className={`user-subscription__container${basic ? ' user-subscription--basic' : ''}`} aria-label="Ваша подписка">
            <div className="user-subscription__header">
                <div className="user-subscription__summary">
                    <div className="user-subscription__status">
                        <h2>Тариф {basic ? 'Базовый' : 'PRO'}</h2>
                        <Bage bageType={trial ? 'trial' : expired ? 'inactive' : 'active'} bagePin={!trial}>
                            {trial ? 'Пробный период' : expired ? 'Неактивен' : 'Активен'}
                        </Bage>
                        {soon && !trial && <Bage bageType="soon" bagePin={false}>Скоро закончится</Bage>}
                    </div>
                    <p className="user-subscription__terms">
                        {basic ? 'Бесплатно · неограниченно дней' : trial ? `Бесплатно${total ? ` · ${total} ${daysLabel(total)}` : ''}`
                            : `${subscription.billingPeriod ? `${annual ? '239' : '399'} ₽ / в месяц · ` : ''}автопродление ${subscription.isAutoRenewal ? 'включено' : 'выключено'}`}
                    </p>
                </div>
                {!basic && Number.isFinite(end) && <div className="user-subscription__date">
                    <p>Дата окончания</p>
                    <time dateTime={subscription.dateEnd!}>{new Intl.DateTimeFormat('ru-RU', {
                        day: 'numeric', month: 'long', year: 'numeric',
                    }).format(end).replace(/ г\.$/, '')}</time>
                </div>}
            </div>
            <div className="user-subscription__counter">
                <div className="user-subscription__track" role="progressbar" aria-label="Прошло времени подписки"
                    aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}>
                    <div style={{ width: `${progress}%` }} />
                </div>
                {!expired && <p>{basic ? 'Осталось ∞ дней' : remaining !== null
                    ? `Осталось ${remaining} ${daysLabel(remaining)}${trial && total ? ` из ${total}` : ''}` : 'Срок подписки уточняется'}</p>}
            </div>
            {showOffer && <div className="user-subscription__offer">
                <p>{trial ? 'Продолжайте пользоваться PRO после окончания пробного периода. Оставшееся время добавим к первому месяцу — вы ничего не теряете.'
                    : failed ? 'Не удалось списать оплату. Проверьте способ оплаты или оплатите подписку снова.'
                    : expired ? <>Подписка закончилась, вы перешли на <strong>Базовый тариф</strong>. Режим Фокус, Матрица и другие функции — недоступны. Заметки и шаблоны сверх лимита — только для чтения.</>
                    : <>Подписка скоро закончится, вы перейдёте на <strong>Базовый тариф</strong>. Режим Фокус, Матрица и другие функции будут недоступны. Заметки и шаблоны сверх лимита — только для чтения.</>}</p>
                <div className="user-subscription__actions">
                    {trial && trialOffer === 'single'
                        ? <Button btnType="filled" title="Оформить PRO сейчас" onClickFunc={() => onPayment(12)} />
                        : <>
                            <Button btnType="default" btnSize="xl" title={annual ? 'Оформить на месяц за 399 ₽' : 'Продлить еще на месяц'} onClickFunc={() => onPayment(1)} />
                            <span className="user-subscription__annual">
                                <div style={{position: 'relative'}}>
                                    <DiscountBage bageRightPos={-8} bageTopPos={-12} bgColor='rgba(172, 152, 246, 1)'>−40%</DiscountBage>
                                    <Button btnType="filled" btnSize="xl" title={annual ? 'Продлить еще на год' : 'Оформить на год за 239 ₽'} onClickFunc={() => onPayment(12)} />
                                </div>
                            </span>
                        </>}
                </div>
            </div>}
        </section>
    );
}

export function UserSubscriptionInfo() {
    const dispatch = useAppDispatch();
    const subscription = useAppSelector(selectSubscription);
    const loading = useAppSelector(selectSubscriptionLoading);
    const error = useAppSelector(selectSubscriptionError);
    const [period, setPeriod] = useState<1 | 12 | null>(null);

    return <>
        {error && <div className="user-subscription__notice" role="alert">{error}
            <Button title="Повторить загрузку" loading={loading === 'pending'} onClickFunc={() => void dispatch(fetchSubscription())} />
        </div>}
        {subscription ? <UserSubscription subscription={subscription} onPayment={setPeriod} />
            : !error && <div className="user-subscription__container" role="status">Загрузка подписки…</div>}
        {period !== null && <PaymentModal initialPeriod={period} isOpenModal closeModalAction={() => setPeriod(null)} />}
    </>;
}
